import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { ExclamationTriangleIcon, InformationCircleIcon } from "@heroicons/react/24/outline";
import { patchBooklet } from "../../services/bookletService";
import { getQuestionFolders } from "../../services/questionService";
import { getApiErrorCode } from "../../utils/apiError";
import { Button } from "../../components/forms/Buttons/Button";
import Select from "../../components/forms/Select/Select";
import { cardClass, linkButtonClass } from "../Exams/TestBuilder/ui";
import {
  bookletFolders,
  btsRound,
  bookletErrorMessage,
  ConflictNote,
  describedBy,
  fieldClass,
  FieldError,
  fieldErrors,
  focusField,
  GRADES,
  invalidClass,
  labelClass,
  LanguageChoice,
  linkTapClass,
  sectionTitleClass,
  tapClass,
  useFocusAfterRender,
  useTakenCodes,
  variantCode,
} from "./bts";
import VariantDigits from "./VariantDigits";

const STALE = ["booklet_changed", "booklet_active", "exam_archived"];
// The form's fields and the booklet's.
const API_FIELD = {
  name: "name",
  description: "description",
  folder: "folder",
  grade: "grade",
  language: "language",
  round: "round_number",
  digits: "variant_numbers",
  duration: "duration_minutes",
};
const FIELD_OF = Object.fromEntries(Object.entries(API_FIELD).map(([field, api]) => [api, field]));
// The element of each field, in the form's order: a failed check focuses the first one with an error.
const FOCUS_ID = {
  name: "bts-set-name",
  description: "bts-set-description",
  folder: "bts-set-folder",
  grade: "bts-set-grade",
  language: "bts-set-language-field",
  round: "bts-set-round",
  duration: "bts-set-duration",
  digits: "bts-set-digits-group",
};
const TITLE_ID = "bts-settings-title";
const SAVE_ID = "bts-settings-save";
// What an active booklet lets change (an activation keeps unsaved changes of these).
export const ACTIVE_FIELDS = ["name", "description"];
const STRUCTURAL = ["folder", "grade", "language"];
const NUMBERS = ["grade", "round", "duration"];

const hasSectionsOf = (booklet) => (booklet.check?.sections?.length || booklet.sections_count || 0) > 0;
// What the booklet lets change: a draft everything (its folder, grade and stream only while it has no subjects),
// an active booklet its name and description, an archived one nothing.
const editableIn = (booklet, field) => {
  if (booklet.status === "archived") return false;
  if (booklet.status !== "draft") return ACTIVE_FIELDS.includes(field);
  return !(hasSectionsOf(booklet) && STRUCTURAL.includes(field));
};

const formOf = (b) => ({
  name: b.name || "",
  description: b.description || "",
  folder: b.folder?.uuid || "",
  grade: b.grade || "",
  language: b.language || "kk",
  round: b.round_number ?? "",
  digits: [...(b.variant_numbers || [])].sort((x, y) => x - y),
  duration: b.duration_minutes ?? "",
});
const same = (a, b) => JSON.stringify(a) === JSON.stringify(b);
// A number typed ("9") is the number the booklet has (9).
const sameValue = (field, a, b) => (NUMBERS.includes(field) && a !== "" && b !== "" ? Number(a) === Number(b) : same(a, b));
const valueOf = (field, value) => {
  if (NUMBERS.includes(field)) return value === "" ? null : Number(value);
  if (field === "name") return value.trim();
  if (field === "description") return value.trim() || null;
  return value;
};

/**
 * The booklet's settings. A draft: everything (its folder, grade and stream only while it has no subjects);
 * an active booklet: its name and description; an archived one: read-only. Unsaved input stays when the booklet
 * is read again, in the fields that can still change. `onDirtyChange("settings", fields)` tells the page which
 * fields have unsaved changes; `takenKey` changing reads the codes other active booklets print again.
 */
export default function BookletSettings({ booklet, onSaved, onStale, onDirtyChange, takenKey }) {
  const [t] = useTranslation();
  const { status } = booklet;
  const draft = status === "draft";
  const archived = status === "archived";
  const hasSections = hasSectionsOf(booklet);

  const [base, setBase] = useState(() => formOf(booklet));
  const [form, setForm] = useState(base);
  const baseRef = useRef(base);
  // The fields the user changed that someone else saved meanwhile too (their value shows under the field).
  const [conflicts, setConflicts] = useState([]);
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);
  const [folders, setFolders] = useState([]);
  const focusAfterRender = useFocusAfterRender();

  // The booklet read again: the fields the user changed keep their input while the booklet still lets them
  // change (a field that became read-only shows the booklet's value), the others show the new values.
  useEffect(() => {
    const next = formOf(booklet);
    const before = baseRef.current;
    const kept = (key) => editableIn(booklet, key) && !sameValue(key, form[key], before[key]);
    setForm(Object.fromEntries(Object.keys(next).map((key) => [key, kept(key) ? form[key] : next[key]])));
    setConflicts((prev) => Object.keys(next).filter((key) => kept(key) && (prev.includes(key) || !sameValue(key, next[key], before[key]))));
    setErrors((prev) => Object.fromEntries(Object.entries(prev).filter(([key]) => editableIn(booklet, key))));
    baseRef.current = next;
    setBase(next);
  }, [booklet]); // eslint-disable-line react-hooks/exhaustive-deps

  // The BTS folders, for a draft without subjects.
  useEffect(() => {
    if (!draft) return undefined;
    const controller = new AbortController();
    let active = true;
    getQuestionFolders({ signal: controller.signal })
      .then((list) => active && setFolders(bookletFolders(Array.isArray(list) ? list : list?.results || [])))
      .catch(() => {});
    return () => {
      active = false;
      controller.abort();
    };
  }, [draft]);

  const editable = (field) => editableIn(booklet, field);
  // Only what the booklet lets change counts (and is sent).
  const changed = Object.keys(API_FIELD).filter((field) => editable(field) && !sameValue(field, form[field], base[field]));
  const dirty = changed.length > 0;
  const changedKey = changed.join(",");
  useEffect(() => {
    onDirtyChange?.("settings", changedKey ? changedKey.split(",") : []);
  }, [changedKey, onDirtyChange]);

  const roundNumber = Number(form.round) || null;
  // An active booklet's codes are its own: no other active booklet can print them.
  const { byDigit } = useTakenCodes({
    round: status === "active" ? null : roundNumber,
    grade: Number(form.grade) || null,
    exclude: booklet.uuid,
    refreshKey: takenKey,
  });

  const folderOptions = useMemo(() => {
    const options = folders.map((f) => ({ value: f.uuid, label: f.name }));
    if (booklet.folder && !options.some((o) => o.value === booklet.folder.uuid)) options.unshift({ value: booklet.folder.uuid, label: booklet.folder.name });
    return options;
  }, [folders, booklet.folder]);
  const gradeOptions = GRADES.map((g) => ({ value: g, label: t("bts.grade_value", { grade: g }) }));
  const folderName = folderOptions.find((o) => o.value === form.folder)?.label || "";
  const folderRound = btsRound(folderName);
  // A number other than the folder's prints another BTS's codes.
  const mismatch = !!folderRound && !!roundNumber && roundNumber !== folderRound;

  const clearError = (field) => {
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: "" }));
    if (formError) setFormError("");
  };
  const set = (field, value) => {
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      // Another BTS folder brings its number, unless the number was changed by hand.
      if (field === "folder") {
        const oldRound = btsRound(folders.find((f) => f.uuid === prev.folder)?.name || booklet.folder?.name);
        const newRound = btsRound(folders.find((f) => f.uuid === value)?.name);
        if (newRound && (prev.round === "" || Number(prev.round) === oldRound)) next.round = newRound;
      }
      return next;
    });
    clearError(field);
  };
  // The value someone else saved, instead of the user's.
  const take = (field) => {
    setForm((prev) => ({ ...prev, [field]: base[field] }));
    setConflicts((prev) => prev.filter((key) => key !== field));
    clearError(field);
    focusField(FOCUS_ID[field]);
  };
  const conflictOn = (field) => conflicts.includes(field) && editable(field) && !sameValue(field, form[field], base[field]);
  const shown = (field, value) => {
    if (value === "" || value === null || value === undefined || (Array.isArray(value) && !value.length)) return t("bts.conflict.empty");
    switch (field) {
      case "name":
      case "description":
        return `«${value}»`;
      case "folder":
        return folderOptions.find((o) => o.value === value)?.label || value;
      case "grade":
        return t("bts.grade_value", { grade: value });
      case "language":
        return t(`bts.language_names.${value}`, { defaultValue: value });
      case "digits":
        return value.map((digit) => variantCode(base.round, base.grade, digit) || digit).join(", ");
      default:
        return String(value);
    }
  };
  const conflictNote = (field) =>
    conflictOn(field) && <ConflictNote id={`${FOCUS_ID[field]}-conflict`} value={shown(field, base[field])} onTake={() => take(field)} disabled={saving} />;
  const describe = (field, ...more) =>
    describedBy(...more, errors[field] && `${FOCUS_ID[field]}-error`, conflictOn(field) && `${FOCUS_ID[field]}-conflict`);

  const validate = () => {
    const next = {};
    if (!form.name.trim()) next.name = t("bts.create.required");
    if (draft) {
      if (!(roundNumber >= 1 && roundNumber <= 9)) next.round = t("bts.create.round_invalid");
      if (!form.digits.length) next.digits = t("bts.digits.none");
      // A booklet always has a duration: an empty field is not "none".
      const duration = form.duration === "" ? NaN : Number(form.duration);
      if (!(Number.isInteger(duration) && duration >= 1 && duration <= 1440)) next.duration = t("bts.create.duration_invalid");
    }
    return next;
  };

  const showErrors = (found) => {
    setErrors(found);
    const first = Object.keys(FOCUS_ID).find((field) => found[field]);
    if (first) focusAfterRender(FOCUS_ID[first], SAVE_ID);
    return !!first;
  };

  const save = async (event) => {
    event.preventDefault();
    if (!dirty || saving) return;
    setFormError("");
    if (showErrors(validate())) return;
    const body = { revision: booklet.revision };
    changed.forEach((field) => {
      body[API_FIELD[field]] = valueOf(field, form[field]);
    });
    // Nothing the booklet lets change differs: nothing is sent, and nothing is said to be saved.
    if (Object.keys(body).length === 1) {
      setForm(base);
      return;
    }
    setSaving(true);
    try {
      const data = await patchBooklet(booklet.uuid, body);
      baseRef.current = formOf(data);
      setForm(formOf(data));
      setConflicts([]);
      onSaved(data);
      toast.success(t("bts.settings.saved"));
      // Save and Cancel go: the focus goes to the card's title.
      focusAfterRender(TITLE_ID);
    } catch (err) {
      const code = getApiErrorCode(err);
      const message = bookletErrorMessage(t, err, "bts.settings.failed");
      if (STALE.includes(code)) {
        onStale(err);
        focusAfterRender(TITLE_ID);
      } else if (code === "booklet_has_sections") {
        showErrors({ folder: message });
      } else if (code === "variant_numbers_invalid") {
        showErrors({ digits: message });
      } else if (code === "round_required") {
        showErrors({ round: message });
      } else {
        const fields = fieldErrors(err);
        const mapped = Object.fromEntries(Object.entries(fields).filter(([api]) => FIELD_OF[api]).map(([api, text]) => [FIELD_OF[api], text]));
        if (!showErrors(mapped)) {
          setFormError(message);
          focusAfterRender(SAVE_ID, TITLE_ID);
        }
      }
    } finally {
      setSaving(false);
    }
  };

  const cancel = () => {
    setForm(base);
    setErrors({});
    setFormError("");
    setConflicts([]);
    document.getElementById(TITLE_ID)?.focus();
  };

  const invalid = (field) => (errors[field] ? invalidClass : "");
  const note = archived ? t("bts.settings.archived_note") : !draft ? t("bts.settings.active_note") : hasSections ? t("bts.settings.sections_note") : "";

  return (
    <section aria-labelledby={TITLE_ID} className={`${cardClass} p-4 sm:p-5`}>
      <h3 id={TITLE_ID} tabIndex={-1} className={`${sectionTitleClass} outline-none`}>
        {t("bts.settings.title")}
      </h3>
      {note && (
        <p className="mt-2 flex items-start gap-2 text-sm text-slate-500">
          <InformationCircleIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          {note}
        </p>
      )}
      <form onSubmit={save} noValidate className="mt-4 space-y-4">
        <div>
          <label htmlFor="bts-set-name" className={labelClass}>
            {t("bts.fields.name")}
          </label>
          <input
            id="bts-set-name"
            type="text"
            maxLength={255}
            value={form.name}
            disabled={!editable("name")}
            onChange={(e) => set("name", e.target.value)}
            aria-invalid={!!errors.name}
            aria-describedby={describe("name")}
            className={`${fieldClass} h-10 max-sm:h-11 ${invalid("name")}`}
          />
          <FieldError id="bts-set-name-error">{errors.name}</FieldError>
          {conflictNote("name")}
        </div>
        <div>
          <label htmlFor="bts-set-description" className={labelClass}>
            {t("bts.fields.description")} <span className="font-normal text-slate-500">({t("bts.optional")})</span>
          </label>
          <textarea
            id="bts-set-description"
            rows={2}
            value={form.description}
            disabled={!editable("description")}
            onChange={(e) => set("description", e.target.value)}
            aria-invalid={!!errors.description}
            aria-describedby={describe("description")}
            className={`${fieldClass} resize-y py-2 ${invalid("description")}`}
          />
          <FieldError id="bts-set-description-error">{errors.description}</FieldError>
          {conflictNote("description")}
        </div>

        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-1 2xl:grid-cols-2">
          <div id="bts-set-folder">
            <Select
              label={t("bts.fields.folder")}
              inputId="bts-set-folder-input"
              ariaInvalid={!!errors.folder}
              ariaErrorMessage={errors.folder ? "bts-set-folder-error" : undefined}
              options={folderOptions}
              value={form.folder}
              isDisabled={!editable("folder")}
              onChange={(option) => set("folder", option?.value || form.folder)}
            />
            <FieldError id="bts-set-folder-error">{errors.folder}</FieldError>
            {conflictNote("folder")}
          </div>
          <div id="bts-set-grade">
            <Select
              label={t("bts.fields.grade")}
              inputId="bts-set-grade-input"
              ariaInvalid={!!errors.grade}
              ariaErrorMessage={errors.grade ? "bts-set-grade-error" : undefined}
              options={gradeOptions}
              value={form.grade}
              isDisabled={!editable("grade")}
              onChange={(option) => set("grade", option?.value || form.grade)}
            />
            <FieldError id="bts-set-grade-error">{errors.grade}</FieldError>
            {conflictNote("grade")}
          </div>
        </div>
        <div id="bts-set-language-field">
          <LanguageChoice id="bts-set-language" value={form.language} onChange={(language) => set("language", language)} disabled={!editable("language")} />
          <FieldError id="bts-set-language-field-error">{errors.language}</FieldError>
          {conflictNote("language")}
        </div>

        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-1 2xl:grid-cols-2">
          <div>
            <label htmlFor="bts-set-round" className={labelClass}>
              {t("bts.fields.round_number")}
            </label>
            <input
              id="bts-set-round"
              type="number"
              inputMode="numeric"
              min={1}
              max={9}
              value={form.round}
              disabled={!editable("round")}
              onChange={(e) => set("round", e.target.value)}
              aria-invalid={!!errors.round}
              aria-describedby={describe("round", mismatch && "bts-set-round-hint")}
              className={`${fieldClass} h-10 max-sm:h-11 ${invalid("round")}`}
            />
            <FieldError id="bts-set-round-error">{errors.round}</FieldError>
            {conflictNote("round")}
          </div>
          <div>
            <label htmlFor="bts-set-duration" className={labelClass}>
              {t("bts.fields.duration")}
            </label>
            <input
              id="bts-set-duration"
              type="number"
              inputMode="numeric"
              min={1}
              max={1440}
              value={form.duration}
              disabled={!editable("duration")}
              onChange={(e) => set("duration", e.target.value)}
              aria-invalid={!!errors.duration}
              aria-describedby={describe("duration")}
              className={`${fieldClass} h-10 max-sm:h-11 ${invalid("duration")}`}
            />
            <FieldError id="bts-set-duration-error">{errors.duration}</FieldError>
            {conflictNote("duration")}
          </div>
        </div>
        {/* Under both columns: the number's note is too long for its own. */}
        {mismatch && (
          <p id="bts-set-round-hint" className="-mt-2 flex items-start gap-2 text-sm text-amber-800">
            <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
            <span className="min-w-0 flex-1 break-words">
              {t("bts.create.round_mismatch", { folder: folderName, folderRound, round: roundNumber })}{" "}
              {editable("round") && (
                <button type="button" onClick={() => set("round", folderRound)} className={`${linkButtonClass} ${linkTapClass} -my-1 py-0.5`}>
                  {t("bts.create.use_folder_round", { round: folderRound })}
                </button>
              )}
            </span>
          </p>
        )}

        <div>
          <VariantDigits
            id="bts-set-digits"
            value={form.digits}
            onChange={(digits) => set("digits", digits)}
            round={roundNumber}
            grade={Number(form.grade) || null}
            taken={byDigit}
            disabled={!editable("digits")}
            error={errors.digits}
          />
          {conflictNote("digits")}
        </div>

        {formError && (
          <p role="alert" className="text-sm text-red-700">
            {formError}
          </p>
        )}
        {/* Only while something is to be saved (an archived booklet never has): no pale disabled Save beside the
            page's one main button, as the subjects' bar. */}
        {dirty && (
          <div className="flex flex-col-reverse gap-2 border-t border-slate-100 pt-4 sm:flex-row sm:justify-end">
            <Button type="button" color="secondary-outline" text={t("buttons.cancel")} onClick={cancel} disabled={saving} className={tapClass} />
            <Button id={SAVE_ID} type="submit" color="primary" text={t("buttons.save")} loading={saving} className={tapClass} />
          </div>
        )}
      </form>
    </section>
  );
}
